import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "@tanstack/react-router";
import Cookies from "js-cookie";

interface AffiliateContextValue {
  affiliateId: string | null;
}

const AffiliateContext = createContext<AffiliateContextValue | null>(null);

const COOKIE_NAME = "am_id";
const COOKIE_EXPIRES_DAYS = 90;

export function AffiliateProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [affiliateId, setAffiliateId] = useState<string | null>(null);

  // On mount or when the query string changes, prefer am_id from URL, else fall back to cookie.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const rawSearch =
      typeof location.search === "string"
        ? location.search
        : window.location.search;
    const searchParams = new URLSearchParams(rawSearch);
    const queryAmId = searchParams.get(COOKIE_NAME);

    if (queryAmId && queryAmId !== affiliateId) {
      setAffiliateId(queryAmId);
      const isHttps = window.location.protocol === "https:";
      Cookies.set(COOKIE_NAME, queryAmId, {
        expires: COOKIE_EXPIRES_DAYS,
        sameSite: "lax",
        secure: isHttps,
        path: "/",
      });
      return;
    }

    // If no query param, try cookie (first mount or navigation without am_id)
    if (!queryAmId && !affiliateId) {
      const cookieValue = Cookies.get(COOKIE_NAME) ?? null;
      if (cookieValue) {
        setAffiliateId(cookieValue);
      }
    }
  }, [location.search, affiliateId]);

  const value = useMemo<AffiliateContextValue>(
    () => ({ affiliateId }),
    [affiliateId],
  );

  return (
    <AffiliateContext.Provider value={value}>
      {children}
    </AffiliateContext.Provider>
  );
}

export function useAffiliateId(): string | null {
  const ctx = useContext(AffiliateContext);
  if (ctx === null) {
    throw new Error("useAffiliateId must be used within an AffiliateProvider");
  }
  return ctx.affiliateId;
}
